import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ConfirmMessageModalContext } from '../confirm-message-modal/confirm-message-modal.component';
import { ModalHelper } from '@services/modal-helper.service';
import { AppState } from '@app/app.service';
import { ToastrHelperService } from '@services/toast-helper.service';
import { ProfileService, UserProfileService } from '@ztarmobile/zwp-service-backend-v2';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { EMAIL_PATTERN } from '@app/app.config';

export type ChangeEmailModalData = {
  userEmail: string,
  customClass?: string,
}

@Component({
  selector: 'app-change-email-modal',
  templateUrl: './change-email-modal.component.html',
  styleUrl: './change-email-modal.component.scss'
})
export class ChangeEmailModalComponent {
  public emailForm: FormGroup;
  public verifyForm: FormGroup;
  public credentialsError = false;
  public showPassword = false;
  public showVerificationForm = false;

  public enteredCode: string = null;
  public form: FormGroup;
  public isRefreshClicked = false;
  public timeLeft = 120000; // 2 Minutes
  public interval = null;
  public seconds: any = 0;
  public minutes: any = 0;
  public incorrectCode = false;
  public profileCode: string;
  public codeIsValid;
  public isValid: boolean;
  public expiredCode: boolean = false;


  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ChangeEmailModalData,
    public dialog: MatDialogRef<ConfirmMessageModalContext>,
    private angularFireService: AngularFireAuth,
    private modalHelper: ModalHelper,
    private appState: AppState,
    private toastHelper: ToastrHelperService,
    private userProfileService: UserProfileService,
    private profileService: ProfileService
  ) { }

  ngOnInit(): void {
    this.emailForm = new FormGroup({
      email: new FormControl('', [Validators.required, Validators.pattern(EMAIL_PATTERN)]),
      password: new FormControl('', [Validators.required])
    });

    this.verifyForm = new FormGroup({
      char1: new FormControl('', [Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z0-9]*$/)])]),
      char2: new FormControl('', [Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z0-9]*$/)])]),
      char3: new FormControl('', [Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z0-9]*$/)])]),
      char4: new FormControl('', [Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z0-9]*$/)])]),
      char5: new FormControl('', [Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z0-9]*$/)])]),
      char6: new FormControl('', [Validators.compose([Validators.required, Validators.pattern(/^[a-zA-Z0-9]*$/)])]),
    });

    this.verifyForm.valueChanges.subscribe(() => {
      
      this.updateConcatenatedValue();
      this.updateFormValidity();
    });
  }

  updateEmailFirebase(newEmail: string) {
    return this.angularFireService.currentUser.then((user) => {
      if (!user) {
        throw new Error('No user is signed in.');
      }
      return user.updateEmail(newEmail);
    }).catch((error) => {
      throw error;
    });
  }

  public updateEmail() {
    this.emailForm.markAllAsTouched()
    if (this.emailForm.invalid) {
      return;
    }

    this.appState.loading = true;
    const email = this.emailForm.get('email').value;
    const password = this.emailForm.get('password').value;
    if (this.data.userEmail !== email) {
      this.appState.loading = true;
      this.angularFireService.signInWithEmailAndPassword(this.data.userEmail, password).then((userCred) => {
        this.updateEmailFirebase(email).then(() => {
          this.profileService.updateUserProfile('v2', { email: email }).then(() => {
            const user = this.angularFireService.currentUser.then((cred) => {
              this.appState.loading = false;
              cred.getIdToken(true);
              this.handleEmailVerification();
            });
          })
        }).catch(() => {
          this.modalHelper.failureModal(
            'Your request cannot be processed at this moment. Please try again later.',
            'Got It',
            'secondary-fail-modal',
            'Something Went Wrong!',
            '',
            'assets/icon/circle-error.svg'
          );
        })
      }).catch(() => {
        this.credentialsError = true;
      });
    } else {
      this.appState.loading = false;
      this.toastHelper.showWarning('Please use a different email for the update');
    }
  }

  public handleEmailVerification(): void {
    this.appState.loading = true;
    this.userProfileService.sendVerificationEmail(this.emailForm.controls.email.value).then(() => {
      this.showVerificationForm = true;
      this.appState.loading = false;
      this.toastHelper.showSuccess('A new verification code has been sent to your email successfully!');
      this.timeLeft = 120000;
      this.timer();
      this.stopTimer();
      this.refreshInterval();
      this.form?.reset();
    }, (error) => {
      this.appState.loading = false;
      this.toastHelper.showAlert(error.error.errors[0].message);
    })
  }

  public async verifyCode() {
    this.verifyForm.markAllAsTouched();
    
    if (this.verifyForm.valid) {
      await this.checkCodeValidity();
      if (this.codeIsValid) {
        this.incorrectCode = false;
        this.expiredCode = false;
        this.closeDialog(this.emailForm.controls.email.value);

        this.modalHelper.successModal(
          '',
          'Done',
          'secondary-success-modal no-massage',
          'Email Successfully Updated',
          '',
          'assets/icon/banner-success-icon.svg',
          true,
        );
      }
    }
  }

  private async checkCodeValidity() {
    this.appState.loading = true;
    await this.profileService.validateEmail('v2', this.enteredCode).then(res => {
      
      this.codeIsValid = true;
      this.expiredCode = false;
      this.incorrectCode = false;
      this.appState.loading = false;
    }, error => {
      this.appState.loading = false;
      this.codeIsValid = false;
      if (error?.error?.errors[0]?.code === 508) {
        this.expiredCode = true;
        this.incorrectCode = false;
      } else {
        this.incorrectCode = true;
        this.expiredCode = false;
      }
    });
  }

  timer(): void {
    this.seconds = Math.floor(this.timeLeft / 1000);
    this.minutes = Math.floor(this.seconds / 60);
    this.minutes %= 60;
    this.seconds %= 60;
    this.minutes = this.minutes < 10 ? '0' + this.minutes : this.minutes;
    this.seconds = this.seconds < 10 ? '0' + this.seconds : this.seconds;
  }

  refreshInterval(): void {
    this.interval = setInterval(() => {
      this.timeLeft = this.timeLeft - 1000;
      if (this.timeLeft > 0) {
        this.timer();
      } else if (this.timeLeft === 0) {
        this.stopTimer();
      }
    }, 1000);
  }

  stopTimer() {
    clearInterval(this.interval);
  }

  updateFormValidity() {
    this.isValid = /^[a-zA-Z0-9]*$/.test(this.enteredCode);
    if (!this.isValid) {
      this.verifyForm.setErrors({ invalid: true });
    } else {
      this.verifyForm.setErrors(null);
    }
  }

  public updateConcatenatedValue() {
    this.enteredCode = Object.values(this.verifyForm.value).join('');
  }

  onInput(event: Event, next: HTMLInputElement) {
    const input = event.target as HTMLInputElement;
    if (input.value.length >= 1) {
      next.focus();
    }
    this.incorrectCode = false;
    this.expiredCode = false;
  }

  onBackspace(event: KeyboardEvent, prev: HTMLInputElement) {
    if (event.key === 'Backspace' && (event.target as HTMLInputElement).value.length === 0) {
      prev.focus();
    }
    this.incorrectCode = false;
    this.expiredCode = false;
  }

  beforeClose(): boolean {
    if (document.body.classList.contains('modal-open')) {
      document.body.classList.remove('modal-open');
    }
    return false;
  }

  closeDialog(res?): void {
    this.beforeClose();
    this.dialog.close(res);
  }

  cancel(): void {
    this.closeDialog(false);
  }
}
